import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useMall } from "@/context/MallContext";
import { useSettings } from "@/context/SettingsContext";
import { shown } from "@/siteMap";
import { accentPair } from "@/lib/contrast";
import "@/theme/simple.css";

const SHOP = { isSimple: false };
const Ctx = createContext({ mode: "simple", isSimple: true, features: {}, ready: false, shop: SHOP });

const isBackOffice = (pathname) => pathname.startsWith("/account") || pathname.startsWith("/admin");

/**
 * Simple | Classic steers the BACK OFFICE only (Phase 10). The storefront is one unified site for every
 * visitor and every shop, so `shop` is the storefront's map context: never Simple, feature flags still count.
 */
export function SiteModeProvider({ children }) {
    const { store, tenantMode, theme, solo } = useMall();
    const settings = useSettings();
    const { pathname, search } = useLocation();
    const onShop = !!store && (!!solo || pathname.startsWith(`/stores/${store.slug}`)
        || new URLSearchParams(search).get("store") === store.slug);
    const [ownerMode, setOwnerMode] = useState(null);
    const mode = ownerMode || (onShop && store.site_mode) || tenantMode || settings?.site_mode || "simple";
    const isSimple = mode === "simple";
    const backOffice = isBackOffice(pathname);
    const features = useMemo(() => settings?.features || {}, [settings]);

    useEffect(() => {
        const root = document.documentElement;
        if (isSimple && backOffice) root.setAttribute("data-mode", "simple"); else root.removeAttribute("data-mode");
    }, [isSimple, backOffice]);

    /** One brand color, with the 4.5:1 rule for the text that sits on it. */
    useEffect(() => {
        const { accent, on } = accentPair(theme?.primary);
        const root = document.documentElement;
        root.style.setProperty("--shop-primary", accent);
        root.style.setProperty("--on-accent", on);
    }, [theme?.primary]);

    const value = useMemo(() => ({
        mode, isSimple, features, ready: !!settings, setOwnerMode, shop: { ...SHOP, features },
    }), [mode, isSimple, features, settings]);
    return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useSiteMode = () => useContext(Ctx);

export const useFeature = (name) => !!useContext(Ctx).features?.[name];

/** Back-office rows: Simple hides collapse + vault, a false flag hides everywhere. */
export const useShown = () => {
    const ctx = useContext(Ctx);
    return (rule) => shown(rule, ctx);
};

/** Storefront rows: only feature flags can hide a row — the site is one layout for everyone. */
export const useShopShown = () => {
    const ctx = useContext(Ctx);
    return (rule) => shown(rule, ctx.shop);
};
